import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { isFetchErrorWithStatus, useAuth } from "@contexts";
import type { Doctor, DoctorBaseAttributes } from "@interfaces";
import { Gender } from "@interfaces";
import {
  BottomActionArea,
  BottomActionButton,
  EmailInput,
  FullScreenLoader,
  PhoneInput,
  TermsAndConditionsCheckBox,
  NavigateBeforeButton,
  SnackBar,
  FormHeader,
  FormContentContainer,
  FormBody,
} from "@components";
import {
  getBirthdateInfo,
  resolveNoBiometricAuthMethod,
  onContactUsClick,
  parseRNPIDoctorBirthdate,
  separateDoctorSpecialty,
} from "@utils";
import { identifyOpenReplaySession } from "@utils/openreplay";
import { NewLoginContactUsParagraph, NewLoginContactUsText } from "../Auth/styled";
import { format } from "rut.js";
import { failedRegistrationWsp } from "@constants";

const FormContainer = styled("form")({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  flex: 1,
});

const SectionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  padding: "16px",
  gap: "8px",
  backgroundColor: theme.palette.background.default,
  borderRadius: "4px",
  border: `1px solid ${theme.palette.border.default}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const FieldText = styled(Typography)(({ theme }) => ({
  textAlign: "left",
  color: theme.palette.text.primary,
}));

export function RegistrationForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkDoctorEmailExists, createDoctor } = useAuth();
  const [validatingData, setValidatingData] = useState<boolean>(false);
  const [creatingDoctor, setCreatingDoctor] = useState<boolean>(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [checkBoxError, setCheckBoxError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    rut = "",
    name = "",
    lastName = "",
    speciality = "",
    university = "",
    region = "",
    commune = "",
    street = "",
    gender = Gender.FEMALE,
    birthdate = "",
    registrationNumber = "",
  }: DoctorBaseAttributes = location.state || {};

  const parsedBirthdate = parseRNPIDoctorBirthdate(birthdate);
  const { years: age } = getBirthdateInfo(birthdate);

  const { mekidocAccessTitle, specialties } = separateDoctorSpecialty(speciality);

  const {
    register,
    handleSubmit,
    getValues,
    control,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
    trigger,
  } = useForm<Doctor>({
    defaultValues: {
      rut,
      name,
      lastName,
      speciality,
      university,
      region,
      commune,
      street,
      gender,
      birthdate: parsedBirthdate ? birthdate : "",
      registrationNumber,
    },
  });

  const [currentPhoneCode, setCurrentPhoneCode] = useState({
    name: "Chile",
    icon: "🇨🇱",
    prefix: "+56",
  });

  const validateForm = useCallback(
    async (data: Doctor) => {
      const fieldNames: (keyof Doctor)[] = ["phone", "email"];
      let isValid = true;

      fieldNames.forEach((fieldName) => {
        if (!data[fieldName]) {
          isValid = false;
        }
      });
      const exists = await checkDoctorEmailExists(data.email);

      if (exists) {
        setError("email", {
          type: "manual",
          message: "Ya existe un usuario con este correo electrónico.",
        });
        isValid = false;
      }

      return isValid;
    },
    [checkDoctorEmailExists, setError],
  );

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsChecked(event.target.checked);
    setCheckBoxError(false);
  };

  const onSubmit: SubmitHandler<Doctor> = useCallback(
    async (data) => {
      if (!termsChecked) {
        setCheckBoxError(true);

        return;
      }

      setValidatingData(true);

      const parsedData = {
        ...data,
        phone: currentPhoneCode.prefix + data.phone,
      };

      const validForm = await validateForm(parsedData);

      if (!validForm) {
        setValidatingData(false);

        return;
      }

      const doctorData = {
        doctor: {
          ...parsedData,
        },
      };

      try {
        setCreatingDoctor(true);
        console.log("Creating doctor request: ", doctorData);
        const authMethod = resolveNoBiometricAuthMethod();
        const doctor = await createDoctor(doctorData, authMethod);

        identifyOpenReplaySession(doctor);
        navigate(`/app/codigo`, {
          state: { doctor, authMethod, isNewAccount: true },
        });
      } catch (error) {
        console.error(error);
        // 409: the RUT already has an account. Registering again would have
        // left the doctor with a second, empty account, so send them to log
        // in instead.
        setErrorMessage(
          isFetchErrorWithStatus(error, [409])
            ? "Ya tienes una cuenta en MekiDoc con este RUT. Vuelve al inicio e ingresa con tu RUT."
            : "No pudimos crear tu cuenta. Inténtalo de nuevo o contáctanos.",
        );
      } finally {
        setCreatingDoctor(false);
        setValidatingData(false);
      }
    },
    [navigate, validateForm, createDoctor, termsChecked, currentPhoneCode],
  );

  if (creatingDoctor) {
    return (
      <>
        <FormHeader
          title="Crear cuenta"
          titleColor="white"
          left={
            <NavigateBeforeButton iconColor="white" onClick={() => navigate("/app")} />
          }
        />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader title="Creando usuario..." />
          </FormBody>
        </FormContentContainer>
      </>
    );
  }

  return (
    <>
      <FormHeader
        title="Crear cuenta"
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={() => navigate("/app")} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <SectionContainer>
              <SectionTitle>Tus datos</SectionTitle>
              <FieldText>
                {name} {lastName}
              </FieldText>
              <FieldText>{format(rut)}</FieldText>
              {parsedBirthdate && (
                <FieldText>
                  {age} años · {parsedBirthdate}
                </FieldText>
              )}
              <FieldText>
                {mekidocAccessTitle} (REG-SIS N°: {registrationNumber})
              </FieldText>
              {specialties && <FieldText>{specialties}</FieldText>}
            </SectionContainer>

            <EmailInput
              register={register}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
              trigger={trigger}
            />

            <PhoneInput
              register={register}
              errors={errors}
              control={control}
              setValue={setValue}
              trigger={trigger}
              clearErrors={clearErrors}
              phoneCodeState={{
                currentPhoneCode,
                setCurrentPhoneCode,
              }}
              showTooltip={true}
            />

            <TermsAndConditionsCheckBox
              termsChecked={termsChecked}
              handleCheckboxChange={handleCheckboxChange}
              error={checkBoxError}
            />

            <NewLoginContactUsParagraph>
              Si necesitas ayuda{" "}
              <NewLoginContactUsText
                onClick={() => onContactUsClick(failedRegistrationWsp)}
              >
                contáctanos
              </NewLoginContactUsText>
            </NewLoginContactUsParagraph>

            <BottomActionArea>
              <BottomActionButton type="submit" disabled={validatingData}>
                {validatingData ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Siguiente"
                )}
              </BottomActionButton>
            </BottomActionArea>
          </FormContainer>
          <SnackBar
            snackBarState={{
              open: !!errorMessage,
              type: "error",
            }}
            onClose={() => setErrorMessage("")}
            alerts={{
              error: errorMessage,
            }}
          />
        </FormBody>
      </FormContentContainer>
    </>
  );
}
